import React, { useMemo } from 'react';
import { CalendarCheck, WarningCircle, Fire } from '@phosphor-icons/react';
import { useLanguage } from '../../contexts/LanguageContext';

const toLocalYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const StatCard = ({ icon: Icon, label, value, tone, testId }) => (
  <div className="rounded-xl bg-white border border-gray-200 p-4 shadow-sm" data-testid={testId}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tone}`}>
      <Icon size={20} weight="duotone" />
    </div>
    <p className="font-display text-3xl font-bold text-gray-900 leading-none">{value}</p>
    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mt-2">{label}</p>
  </div>
);

const TaskRow = ({ task, onSelectTask }) => (
  <button
    onClick={() => onSelectTask(task.id)}
    data-testid={`glance-task-${task.id}`}
    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-blue-50 text-left transition-colors"
  >
    <span className="flex items-center gap-2 min-w-0">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${task.priority === 'High' ? 'bg-red-500' : task.priority === 'Medium' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
      <span className="truncate text-sm font-medium text-gray-900">{task.title}</span>
    </span>
    <span className="text-xs text-gray-500 flex-shrink-0">{task.time || ''}</span>
  </button>
);

export const TodayGlance = ({ tasks, onSelectTask, briefing }) => {
  const { t } = useLanguage();
  const tr = (key, fallback) => (t(key) === key ? fallback : t(key));

  const { dueToday, overdue, highOpen } = useMemo(() => {
    const today = toLocalYMD(new Date());
    const open = tasks.filter((task) => !task.completed && !task.archived && task.due_date);
    const day = (task) => String(task.due_date).slice(0, 10);
    return {
      dueToday: open.filter((task) => day(task) === today).sort((a, b) => (a.time || '').localeCompare(b.time || '')),
      overdue: open.filter((task) => day(task) < today),
      highOpen: open.filter((task) => task.priority === 'High'),
    };
  }, [tasks]);

  return (
    <div className="space-y-5" data-testid="today-glance">
      <div>
        <p className="text-xs uppercase tracking-wider font-medium text-gray-500">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        <h2 className="font-display text-2xl font-bold text-gray-900 tracking-tight mt-1">{tr('todayAtAGlance', 'Today at a glance')}</h2>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={CalendarCheck} label={tr('dueToday', 'Due today')} value={dueToday.length} tone="bg-blue-50 text-blue-600" testId="glance-due-today-count" />
        <StatCard icon={WarningCircle} label={tr('overdue', 'Overdue')} value={overdue.length} tone="bg-red-50 text-red-600" testId="glance-overdue-count" />
        <StatCard icon={Fire} label={tr('highPriority', 'High priority')} value={highOpen.length} tone="bg-amber-50 text-amber-600" testId="glance-high-count" />
      </div>
      <div className="rounded-xl bg-white border border-gray-200 p-3 shadow-sm">
        <p className="px-3 pt-1 pb-2 text-sm font-semibold text-gray-900">{tr('dueToday', 'Due today')}</p>
        {dueToday.length === 0 ? (
          <p className="px-3 pb-2 text-sm text-gray-500" data-testid="glance-nothing-due">{tr('nothingDueToday', 'Nothing due today. Select a task on the left to see its details.')}</p>
        ) : (
          dueToday.slice(0, 6).map((task) => <TaskRow key={task.id} task={task} onSelectTask={onSelectTask} />)
        )}
      </div>
      {briefing}
      <p className="text-xs text-gray-400" data-testid="glance-keyboard-hint">
        {tr('keyboardHint', 'Tip: use ↑ ↓ to move through your tasks, Enter to open, Esc to close.')}
      </p>
    </div>
  );
};

export default TodayGlance;
